// ════════════════════════════════════════════════════════════════════
// HEALTHSHERPA APPLICATION-EXPORT PARSER
//
// HealthSherpa exports a 188-column CSV per agent of ALL applications
// ever submitted on their NPN. Our older code routed this file through
// the generic CSV path, which captured ~12 columns and lost the rest.
// This parser knows the schema and surfaces the rich axes an agency
// operator actually runs the book on:
//
//   IDENTITY:   first_name, last_name, suffix, gender, dob, ssn,
//               phone, email, address (+ unit, city, state, zip, county)
//   PLAN:       plan, plan_name, plan_hios_id, csr_level, metal_level,
//               issuer, effective_date, premium, subsidy, net_premium,
//               health_premium, dental_premium
//   APP IDS:    healthsherpa_id (UNIQUE per app), ffm_app_id (CMS),
//               ffm_subscriber_id, alliance_id, transaction_id,
//               issuer_assigned_policy_id, issuer_assigned_subscriber_id
//   LIFECYCLE:  policy_status, effectuated, date_effectuated,
//               date_cancelled, expiration_date,
//               ltv_premier_effectuation_status, ltv_premier_policy_status,
//               grace_period_start_date, paid_through_date,
//               past_due_member_responsibility_balance_due,
//               current_member_responsibility_balance_due,
//               policy_status_notes (cancellation reason — gold)
//   COMPLIANCE: dmi_outstanding_count, dmi_expired_count,
//               svi_outstanding_count, svi_expired_count,
//               consent_status, consent_updated_at,
//               review_status, review_updated_at
//   COMMISSION: agent, npn_used, npn_reason, policy_aor,
//               dental_policy_aor, agent_agency_name,
//               submitting_agent_name, submitting_agent_npn
//   HOUSEHOLD:  applicant_count, household_size, household_income,
//               spouse_* + other_1_* through other_10_* (full schema)
//   DENTAL:     dental_plan_name, dental_plan_hios_id,
//               dental_plan_policy_status
//   RENEWAL:    renewal, is_renewable, renew_resume_link
//
// DETECTION SIGNATURE
// ──────────────────────────────────────────────────────────────────
// The presence of all three { csr_level, ffm_app_id, healthsherpa_id }
// in the header row is unique to HealthSherpa. Generic CSVs and Plan
// Advisors / Jarvis / HCSC never carry these together.
//
// PRIMARY DEDUP KEY
// ──────────────────────────────────────────────────────────────────
// healthsherpa_id is HealthSherpa's unique application identifier.
// Re-importing the same export should UPDATE every row, never
// duplicate. We expose it as member_id on the health_policies upsert
// so the (owner, member_id, carrier) Rule-1 match in clientMatcher
// fires hard.
//
// HOUSEHOLD HANDLING (v1)
// ──────────────────────────────────────────────────────────────────
// Each row covers the PRIMARY APPLICANT only at write time. Spouse +
// other_N_* members are captured on the row in `_household` so the
// importer / preview UI can show "1 application · 4 members" without
// inserting four separate client rows. v2 will optionally fan out
// household members into linked client records.
// ════════════════════════════════════════════════════════════════════

const SIGNATURE = ['csr_level', 'ffm_app_id', 'healthsherpa_id', 'policy_aor'];

// ── policy_status → health_policies.status mapping ──────────────
const STATUS_MAP = {
  'Effectuated':         'active',
  'PendingEffectuation': 'pending',
  'Pending':             'pending',
  'Cancelled':           'cancelled',
  'Terminated':          'cancelled',
  'Unknown':             'pending',
  '':                    'pending',
};

// ── CSV splitter (handles quoted fields with embedded commas / quotes) ─
function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cur += ch;
    } else {
      if (ch === ',') { out.push(cur); cur = ''; }
      else if (ch === '"') inQ = true;
      else cur += ch;
    }
  }
  out.push(cur);
  return out;
}

// CSV files in the wild can contain embedded \r\n inside quoted cells.
// Splitting on /\r?\n/ corrupts those rows. Use a quote-aware splitter
// for safety on the full text first.
function splitCsvLines(text) {
  const lines = [];
  let cur = '';
  let inQ = false;
  const s = String(text || '');
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '"') { inQ = !inQ; cur += ch; continue; }
    if ((ch === '\n' || ch === '\r') && !inQ) {
      if (cur) lines.push(cur);
      cur = '';
      // Skip the \n in \r\n
      if (ch === '\r' && s[i + 1] === '\n') i++;
      continue;
    }
    cur += ch;
  }
  if (cur) lines.push(cur);
  return lines;
}

export function isHealthSherpaFormat(text) {
  // Need the header on line 1 or 2. HealthSherpa always has the header
  // on row 1, so check the first 1024 chars for the signature trio.
  const head = String(text || '').slice(0, 4096).toLowerCase();
  return SIGNATURE.every(s => head.includes(s.toLowerCase()));
}

// ── String helpers ──────────────────────────────────────────────
function titleCase(s) {
  return String(s || '').toLowerCase().replace(/\b([a-z])/g, (_, c) => c.toUpperCase());
}

// "Curry, Harrison" → "Harrison Curry" (title-cased)
function flipNameLastFirst(s) {
  const str = String(s || '').trim();
  if (!str) return '';
  if (str.includes(',')) {
    const [last, first] = str.split(',').map(t => t.trim());
    return titleCase(`${first} ${last}`.trim());
  }
  return titleCase(str);
}

function parsePolicyAor(raw) {
  // "Harrison Curry (NPN: 21304100)" → { name: "Harrison Curry", npn: "21304100" }
  const s = String(raw || '').trim();
  if (!s) return null;
  const m = /^(.+?)\s*\(NPN:\s*(\d+)\)\s*$/i.exec(s);
  if (m) return { name: m[1].trim(), npn: m[2] };
  return { name: s, npn: null };
}

// Coerce a date string to ISO YYYY-MM-DD. Accepts MM/DD/YYYY,
// M/D/YYYY, or already-ISO inputs. Returns null on bad input.
function parseDate(s) {
  if (!s) return null;
  const str = String(s).trim();
  if (!str) return null;
  // Already ISO?
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(str);
  if (m) {
    const mm = String(m[1]).padStart(2, '0');
    const dd = String(m[2]).padStart(2, '0');
    return `${m[3]}-${mm}-${dd}`;
  }
  return null;
}

function parseMoney(s) {
  if (s == null || s === '') return null;
  const n = Number(String(s).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function parseIntSafe(s) {
  const n = parseInt(String(s || '0'), 10);
  return Number.isFinite(n) ? n : 0;
}

function parseBool(s) {
  const v = String(s || '').trim().toLowerCase();
  return v === 'true' || v === 'yes' || v === 'y' || v === '1';
}

// Build the household array for a single application: primary +
// spouse + other_1..other_10 (where applicable).
function buildHousehold(get) {
  const out = [];
  const fn = get('first_name'); const ln = get('last_name');
  if (fn || ln) {
    out.push({
      role: 'primary',
      first: titleCase(fn),
      last:  titleCase(ln),
      dob:   get('dob') || null,
      applying: parseBool(get('applying')),
    });
  }
  // Spouse.
  const sfn = get('spouse_first_name'); const sln = get('spouse_last_name');
  if (sfn || sln) {
    out.push({
      role: 'spouse',
      first: titleCase(sfn),
      last:  titleCase(sln),
      dob:   get('spouse_dob') || null,
      applying: parseBool(get('spouse_applying')),
    });
  }
  // other_1 .. other_10
  for (let i = 1; i <= 10; i++) {
    const ofn = get(`other_${i}_first_name`);
    const oln = get(`other_${i}_last_name`);
    if (!ofn && !oln) continue;
    out.push({
      role: `other_${i}`,
      first: titleCase(ofn),
      last:  titleCase(oln),
      dob:   get(`other_${i}_dob`) || null,
      applying: parseBool(get(`other_${i}_applying`)),
    });
  }
  return out;
}

// ── The main parser ─────────────────────────────────────────────
// Returns { rows, agent, summary }.
//
// `rows` carry the same field names the existing generic-CSV /
// dedupe-planner pipeline already consumes (client_name, client_phone,
// client_email, client_dob, carrier, plan_name, plan_type, member_id,
// monthly_premium, monthly_commission, subsidy_amount, effective_date,
// renewal_date, termination_date, status, lead_source, notes,
// agent_prod_num) plus a `_source: 'healthsherpa'` audit field and
// the rich HealthSherpa-only metadata.
export function parseHealthSherpaCsv(text) {
  const lines = splitCsvLines(text).map(l => l.trimEnd()).filter(Boolean);
  if (lines.length < 2) return emptyResult();

  const header = parseCsvLine(lines[0]).map(h => h.trim());
  const col = {};
  header.forEach((h, i) => { col[h.toLowerCase()] = i; });

  const rows = [];
  let agentMeta = null;
  const summary = {
    totalRows:     0,
    byStatus:      {},
    byCarrier:     {},
    byMetalLevel:  {},
    byPolicyAor:   {},
    totalGrossPremium:  0,
    totalSubsidy:       0,
    totalNetPremium:    0,
    totalHealthMembers: 0,
    pastDueRows:        0,
    pastDueAmount:      0,
    gracePeriodRows:    0,
    dmiOutstanding:     0,
    dmiExpired:         0,
    sviOutstanding:     0,
    sviExpired:         0,
    consentMissing:     0,
    aepRollCount:       0,    // rows with renewal=true OR (terminated 2025 + this row 2026)
    cancellationReasons: {},
    nonAgentAorCount:   0,    // policy_aor name doesn't match agent name
    archivedCount:      0,
  };

  // Track the operator's identity so AOR-mismatch can be detected.
  let operatorName = null;
  let operatorNpn  = null;

  for (let i = 1; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw || !raw.trim()) continue;
    const cells = parseCsvLine(raw);
    const get = (k) => {
      const ix = col[String(k).toLowerCase()];
      return ix != null && ix >= 0 ? (cells[ix] || '').trim() : '';
    };

    // Skip rows with no identity at all.
    if (!get('first_name') && !get('last_name') && !get('healthsherpa_id')) continue;

    // Capture agent identity once.
    if (!agentMeta) {
      const agentRaw = get('agent') || get('submitting_agent_name');
      const npn      = get('npn_used') || get('submitting_agent_npn');
      const flipped  = flipNameLastFirst(agentRaw);
      if (flipped || npn) {
        agentMeta = {
          name:      flipped || null,
          npn:       npn || null,
          agency:    get('agent_agency_name') || null,
          npnReason: get('npn_reason') || null,
        };
        operatorName = flipped ? flipped.toLowerCase() : null;
        operatorNpn  = npn || null;
      }
    }

    const policyStatus = get('policy_status') || 'Unknown';
    const status       = STATUS_MAP[policyStatus] || 'pending';

    const carrier   = get('issuer');
    const metal     = get('metal_level');
    const csr       = get('csr_level') || 'none';
    const planName  = get('plan_name') || get('plan');
    const planHios  = get('plan_hios_id');
    const effective = parseDate(get('effective_date'));
    const cancelled = parseDate(get('date_cancelled'));
    const expiration= parseDate(get('expiration_date'));
    const effectuated = parseDate(get('date_effectuated'));
    const paidThrough = parseDate(get('paid_through_date'));
    const gracePeriod = parseDate(get('grace_period_start_date'));

    const aorRaw = get('policy_aor');
    const aor    = parsePolicyAor(aorRaw);

    const grossPremium = parseMoney(get('gross_premium')) ?? parseMoney(get('premium'));
    const subsidy      = parseMoney(get('subsidy'));
    const netPremium   = parseMoney(get('net_premium'));
    const dentalPremium= parseMoney(get('dental_premium'));
    const pastDue      = parseMoney(get('past_due_member_responsibility_balance_due'));
    const currentDue   = parseMoney(get('current_member_responsibility_balance_due'));

    const applicantCount = parseIntSafe(get('applicant_count')) || 1;
    const householdSize  = parseIntSafe(get('household_size')) || applicantCount;

    const dmiOut = parseIntSafe(get('dmi_outstanding_count'));
    const dmiExp = parseIntSafe(get('dmi_expired_count'));
    const sviOut = parseIntSafe(get('svi_outstanding_count'));
    const sviExp = parseIntSafe(get('svi_expired_count'));

    const consentStatus = get('consent_status');
    const reviewStatus  = get('review_status');
    const notesRaw      = get('policy_status_notes');
    const renewal       = parseBool(get('renewal'));
    const isRenewable   = parseBool(get('is_renewable'));
    const archived      = parseBool(get('archived'));

    const household = buildHousehold(get);

    // Build the unified row shape the planner consumes.
    const row = {
      _source: 'healthsherpa',
      // ── source fields kept for audit + UI preview ──
      _healthsherpaId:   get('healthsherpa_id'),
      _ffmAppId:         get('ffm_app_id'),
      _ffmSubscriberId:  get('ffm_subscriber_id'),
      // Carrier-side IDs — these are what commission statements
      // (Plan Advisors, HCSC, future) carry as the "Policy" field.
      // We index them so a later commission-statement import lands
      // an UPDATE on the same health_policies row, never a new
      // duplicate. The BoB↔commission cross-reference depends on
      // this linkage.
      _carrierIds: {
        issuerAssignedPolicyId:      get('issuer_assigned_policy_id'),
        issuerAssignedSubscriberId:  get('issuer_assigned_subscriber_id'),
        issuerAssignedPrimaryMemberId: get('issuer_assigned_primary_member_id'),
        carrierProvidedIdentifier:   get('carrier_provided_identifier'),
      },
      _planStatus:       policyStatus,
      _ltvPolicyStatus:  get('ltv_premier_policy_status'),
      _ltvEffStatus:     get('ltv_premier_effectuation_status'),
      _metalLevel:       metal,
      _csrLevel:         csr,
      _planHiosId:       planHios,
      _aor:              aor,
      _dentalAor:        get('dental_policy_aor'),
      _policyStatusNotes:notesRaw,
      _consentStatus:    consentStatus,
      _reviewStatus:     reviewStatus,
      _renewal:          renewal,
      _isRenewable:      isRenewable,
      _archived:         archived,
      _submissionDate:   parseDate(get('submission_date')),
      _submissionType:   get('submission_type'),
      _gracePeriod:      gracePeriod,
      _paidThrough:      paidThrough,
      _pastDueAmt:       pastDue,
      _currentDueAmt:    currentDue,
      _dental: {
        planName:  get('dental_plan_name'),
        hiosId:    get('dental_plan_hios_id'),
        status:    get('dental_plan_policy_status'),
        premium:   dentalPremium,
      },
      _compliance: {
        dmiOutstanding:  dmiOut,
        dmiExpired:      dmiExp,
        dmiCompleted:    parseIntSafe(get('dmi_completed_count')),
        sviOutstanding:  sviOut,
        sviExpired:      sviExp,
        sviCompleted:    parseIntSafe(get('svi_completed_count')),
      },
      // ── flattened convenience fields the preview UI consumes
      // directly (mirror of nested data so the JSX stays terse).
      _planName:         planName || null,
      _netPremium:       netPremium,
      _subsidy:          subsidy,
      _pastDueAmount:    pastDue || 0,
      _dmiOutstanding:   dmiOut,
      _sviOutstanding:   sviOut,
      _policyAorName:    aor?.name || null,
      _aorIsOperator:    aor && operatorName ? aor.name.toLowerCase() === operatorName : null,
      _household: household,
      _householdSize:    householdSize,
      _applicantCount:   applicantCount,
      _householdIncome:  get('household_income'),
      _preferredLanguage:get('preferred_language'),
      _ethnicity:        get('ethnicity'),

      // ── health_policies upsert payload (matches generic-CSV path) ──
      client_name:        titleCase(`${get('first_name')} ${get('last_name')}`).trim(),
      client_phone:       get('phone') || null,
      client_email:       get('email') || null,
      client_dob:         null, // dob is masked in agent-side exports
      // Address columns — populated structurally, never dumped into notes.
      client_address:     get('address')      || get('street_address') || null,
      client_address2:    get('address_unit') || get('unit')           || get('apt') || null,
      client_city:        get('city')         || null,
      client_state:       get('state')        || null,
      client_zip:         get('zip')          || get('zip_code')       || null,
      client_county:      get('county')       || null,
      carrier:            carrier || null,
      plan_name:          planName || null,
      plan_type:          'aca_marketplace',
      // member_id strategy: prefer the carrier-assigned policy ID
      // (this is what Plan Advisors / HCSC / etc. commission
      // statements carry as "Policy"), falling back to the
      // HealthSherpa internal ID. This makes Rule-1 of the matcher
      // catch the SAME policy across BoB import + commission import,
      // so updating last_commission_at and surfacing silent
      // disenrollment works out of the box.
      member_id:          get('issuer_assigned_policy_id')
                       || get('carrier_provided_identifier')
                       || get('healthsherpa_id')
                       || get('ffm_subscriber_id')
                       || null,
      monthly_premium:    grossPremium,
      monthly_commission: null,
      subsidy_amount:     subsidy,
      effective_date:     effective,
      renewal_date:       expiration,
      termination_date:   cancelled,
      last_commission_at: paidThrough || effectuated,
      status,
      lead_source:        'HealthSherpa',
      agent_prod_num:     get('npn_used') || get('submitting_agent_npn') || null,
      notes:              [
        get('plan') ? `Plan: ${get('plan')}` : null,
        metal ? `Metal: ${metal}` : null,
        csr && csr !== 'none' ? `CSR: ${csr}` : null,
        get('ffm_app_id') ? `FFM App: ${get('ffm_app_id')}` : null,
        notesRaw ? `Status note: ${notesRaw}` : null,
      ].filter(Boolean).join(' · ') || null,
    };

    rows.push(row);

    // ── Roll-up summary ──
    summary.totalRows++;
    summary.byStatus[policyStatus] = (summary.byStatus[policyStatus] || 0) + 1;
    if (carrier)         summary.byCarrier[carrier]    = (summary.byCarrier[carrier]   || 0) + 1;
    if (metal)           summary.byMetalLevel[metal]   = (summary.byMetalLevel[metal]  || 0) + 1;
    if (aor?.name)       summary.byPolicyAor[aor.name] = (summary.byPolicyAor[aor.name]|| 0) + 1;
    if (grossPremium)    summary.totalGrossPremium    += grossPremium;
    if (subsidy)         summary.totalSubsidy         += subsidy;
    if (netPremium)      summary.totalNetPremium      += netPremium;
    summary.totalHealthMembers += applicantCount;
    if (pastDue && pastDue > 0)   { summary.pastDueRows++; summary.pastDueAmount += pastDue; }
    if (gracePeriod)              { summary.gracePeriodRows++; }
    summary.dmiOutstanding += dmiOut;
    summary.dmiExpired     += dmiExp;
    summary.sviOutstanding += sviOut;
    summary.sviExpired     += sviExp;
    if (!consentStatus || consentStatus === 'none_present') summary.consentMissing++;
    if (renewal) summary.aepRollCount++;
    if (notesRaw) summary.cancellationReasons[notesRaw] = (summary.cancellationReasons[notesRaw] || 0) + 1;
    if (aor && operatorName && aor.name.toLowerCase() !== operatorName) summary.nonAgentAorCount++;
    if (archived) summary.archivedCount++;
  }

  return { rows, agent: agentMeta, summary };
}

function emptyResult() {
  return {
    rows: [],
    agent: null,
    summary: {
      totalRows: 0, byStatus: {}, byCarrier: {}, byMetalLevel: {}, byPolicyAor: {},
      totalGrossPremium: 0, totalSubsidy: 0, totalNetPremium: 0, totalHealthMembers: 0,
      pastDueRows: 0, pastDueAmount: 0, gracePeriodRows: 0,
      dmiOutstanding: 0, dmiExpired: 0, sviOutstanding: 0, sviExpired: 0,
      consentMissing: 0, aepRollCount: 0, cancellationReasons: {},
      nonAgentAorCount: 0, archivedCount: 0,
    },
  };
}
